import React, { useState, useMemo, useEffect } from "react";
import axios from "axios";
import {
  Wallet,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Calendar,
  CheckCircle2,
  Clock,
  Printer,
  Download,
  Plus,
  Search,
  ChevronRight,
  Filter,
  CreditCard,
  Building,
  DollarSign,
  Receipt,
  FileText,
  MessageCircle,
  Users,
  ShieldCheck,
  RefreshCw,
  PieChart as PieChartIcon,
  BarChart2,
  ArrowUpRight,
  ArrowDownRight,
  Eye,
  Trash2,
  X,
  Percent,
  Zap
} from "lucide-react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  PieChart,
  Pie,
  Cell
} from "recharts";
import { ReceiptModal } from "./ReceiptModal";
import { PromissoryNoteModal } from "./PromissoryNoteModal";
import EdesisGelirListesi from "./EdesisGelirListesi";
import EdesisVadeTakvimi from "./EdesisVadeTakvimi";
import EdesisTahsilatListesi from "./EdesisTahsilatListesi";
import { OverviewTab } from "./tabs/OverviewTab";
import { IncomesTab } from "./tabs/IncomesTab";
import { OverdueAgingTab } from "./tabs/OverdueAgingTab";
import { ScheduleCalendarTab } from "./tabs/ScheduleCalendarTab";
import { CollectionsTab } from "./tabs/CollectionsTab";
import { ExpensesTab } from "./tabs/ExpensesTab";
import { BankPortfolioStrip, CashBankLedgerTab } from "./tabs/CashBankLedgerTab";
import { BackupRestoreTab, RestoreConfirmModal } from "./tabs/BackupRestoreTab";
import { SenetlerTab, QuickPaymentModal, ExpenseModal } from "./tabs/MuhasebeQuickModals";
import { MuhasebeKpiGrid } from "./tabs/MuhasebeKpiGrid";
import AccountingService, { ENABLE_KASA_BANKA } from "../lib/edesisAccountingService";
import { trIncludes, formatTrtDateTime } from "../lib/utils";

import {
  getStudentCategory,
  fmtMoney,
  getEffectiveTotalFee,
  computeStudentPlan,
  getPaymentAccountInfo,
  getExpenseAccountInfo,
  exportAccountingDataToExcel
} from "./tabs/muhasebeHelpers";

export {
  getStudentCategory,
  fmtMoney,
  getEffectiveTotalFee,
  computeStudentPlan,
  getPaymentAccountInfo,
  getExpenseAccountInfo
};

export function MuhasebeV2Dashboard({
  me,
  data = {},
  onOpen,
  onSelectStudent,
  API,
  reload,
  activeTab: propActiveTab,
  onTabChange,
  onNavigate
}) {
  const [internalTab, setInternalTab] = useState(() => {
    if (propActiveTab) return propActiveTab;
    try {
      if (typeof window !== "undefined" && window.location.hash.includes("payments")) {
        const saved = sessionStorage.getItem("cozum_muhasebe_v2_tab") || localStorage.getItem("cozum_muhasebe_v2_tab");
        if (saved) return saved;
      }
    } catch {}
    return "dashboard";
  });
  const [statusQuickFilter, setStatusQuickFilter] = useState("all"); // 'all' | 'paid' | 'unpaid' | 'overdue'

  useEffect(() => {
    if (propActiveTab !== undefined) {
      setInternalTab(propActiveTab);
      try {
        sessionStorage.setItem("cozum_muhasebe_v2_tab", propActiveTab);
        localStorage.setItem("cozum_muhasebe_v2_tab", propActiveTab);
      } catch {}
    }
  }, [propActiveTab]);

  // Backup state
  const [backups, setBackups] = useState([]);
  const [loadingBackups, setLoadingBackups] = useState(false);
  const [restoreModalBackup, setRestoreModalBackup] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [backupNote, setBackupNote] = useState("");
  const [backupAlert, setBackupAlert] = useState("");
  const activeTab = propActiveTab !== undefined ? propActiveTab : internalTab;
  const setActiveTab = (tab) => {
    setInternalTab(tab);
    try {
      sessionStorage.setItem("cozum_muhasebe_v2_tab", tab);
      localStorage.setItem("cozum_muhasebe_v2_tab", tab);
    } catch {}
    if (onTabChange) onTabChange(tab);
  };
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRegisterFilter, setSelectedRegisterFilter] = useState("all");
  const [agingFilter, setAgingFilter] = useState("all"); // 'all' | '1-30' | '31-60' | '60+'

  // Modals state
  const [receiptPayment, setReceiptPayment] = useState(null);
  const [promissoryStudent, setPromissoryStudent] = useState(null);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const expenseId=React.useRef();
  const expenseSaving=React.useRef(false);
  const [savingExpense,setSavingExpense]=useState(false);
  useEffect(()=>{if(showExpenseModal)expenseId.current="exp_"+crypto.randomUUID();},[showExpenseModal]);
  const [showQuickPaymentModal, setShowQuickPaymentModal] = useState(false);
  const paymentId=React.useRef();
  useEffect(()=>{if(showQuickPaymentModal)paymentId.current="pay_"+crypto.randomUUID();},[showQuickPaymentModal]);

  const handleOpenUnifiedContract = (target) => {
    const targetNo = typeof target === "string"
      ? target
      : (target?.sozlesmeNo || target?.gelirKodu || target?.ogrenciAdiSoyadi || target?.student_name || target?.student?.student_name);
    if (targetNo) {
      try {
        sessionStorage.setItem("cozum_open_contract_no", targetNo);
        sessionStorage.setItem("cozum_contract_prev_page", "payments");
      } catch {}
    }
    if (onNavigate) {
      onNavigate("documents");
    }
  };

  const loadBackups = async () => {
    setLoadingBackups(true);
    try {
      const res = await axios.get(`${API}/backup`);
      setBackups(res.data?.backups || []);
    } catch (err) {
      console.error("Backups load error:", err);
    } finally {
      setLoadingBackups(false);
    }
  };

  useEffect(() => {
    if (activeTab === "backup") {
      loadBackups();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const handleTakeManualBackup = async () => {
    try {
      setLoadingBackups(true);
      await axios.post(`${API}/backup`, { notes: backupNote || "Manuel Yönetici Snapshot Yedeği" });
      setBackupNote("");
      setBackupAlert("✓ Yeni veritabanı yedeği başarıyla oluşturuldu.");
      await loadBackups();
      setTimeout(() => setBackupAlert(""), 4000);
    } catch (err) {
      alert("Yedek alınırken hata oluştu: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoadingBackups(false);
    }
  };

  const handleConfirmRestore = async () => {
    if (!restoreModalBackup) return;
    setRestoring(true);
    try {
      const res = await axios.post(`${API}/backup/restore`, { backup_id: restoreModalBackup.id });
      setRestoreModalBackup(null);
      setBackupAlert("✓ Veritabanı başarıyla geri yüklendi! Sayfa verileri güncelleniyor...");
      if (reload) reload();
      await loadBackups();
      setTimeout(() => setBackupAlert(""), 5000);
    } catch (err) {
      alert("Geri yükleme hatası: " + (err.response?.data?.detail || err.message));
    } finally {
      setRestoring(false);
    }
  };

  const handleUploadBackupJson = async (file) => {
    if (!file) return;
    try {
      setRestoring(true);
      const text = await file.text();
      let parsed;
      try {
        parsed = JSON.parse(text);
      } catch {
        alert("Seçilen dosya geçerli bir JSON dosyası değil.");
        return;
      }

      if (!parsed || typeof parsed.tables !== "object") {
        alert("Geçersiz yedek dosyası: 'tables' anahtarı bulunamadı.");
        return;
      }

      const tableKeys = Object.keys(parsed.tables);
      const studentCount = parsed.tables.students?.length || 0;
      const paymentCount = parsed.tables.payments?.length || 0;
      const contractCount = parsed.tables.contracts?.length || 0;

      const confirmMsg = `"${file.name}" yedeği sisteme aktarılacak.\n\nİçerik Özeti:\n- Öğrenci: ${studentCount}\n- Tahsilat: ${paymentCount}\n- Sözleşme: ${contractCount}\n- Toplam Tablo: ${tableKeys.length}\n\nBu işlem mevcut veritabanını bu yedek ile tamamen güncelleyecektir (işlem öncesi otomatik güvenlik yedeği alınır).\n\nOnaylıyor musunuz?`;
      if (!window.confirm(confirmMsg)) return;

      await axios.post(`${API}/backup/upload-restore`, {
        snapshot: parsed,
        notes: `Harici dosya yüklemesi (${file.name})`
      });

      setBackupAlert("✓ Harici yedek dosyası başarıyla yüklendi ve veritabanı geri yüklendi!");
      if (reload) reload();
      await loadBackups();
      setTimeout(() => setBackupAlert(""), 5000);
    } catch (err) {
      alert("Yedek yükleme hatası: " + (err.response?.data?.detail || err.response?.data?.error || err.message));
    } finally {
      setRestoring(false);
    }
  };

  // Excel / CSV Export for system data and snapshots
  const exportDataToExcel = (customData = null, fileSuffix = "Tum_Sistem") => {
    exportAccountingDataToExcel(customData, fileSuffix, data);
  };

  const handleDownloadBackupJson = async (b) => {
    try {
      const res = await axios.get(`${API}/backup?download=${b.id}`);
      const dataStr = typeof res.data === "string" ? res.data : JSON.stringify(res.data, null, 2);
      const blob = new Blob([dataStr], { type: "application/json;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${b.id}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      window.open(`${API}/backup?download=${b.id}`, "_blank");
    }
  };

  const handleDownloadBackupExcel = async (b) => {
    try {
      setLoadingBackups(true);
      const res = await axios.get(`${API}/backup?download=${b.id}`);
      const snap = typeof res.data === "string" ? JSON.parse(res.data) : res.data;
      exportDataToExcel(snap, b.id);
    } catch (e) {
      exportDataToExcel(null, b?.id || "Yedek");
    } finally {
      setLoadingBackups(false);
    }
  };

  // New Expense form
  const [expTitle, setExpTitle] = useState("");
  const [expAmount, setExpAmount] = useState("");
  const [expCategory, setExpCategory] = useState("Kira");
  const [expAccount, setExpAccount] = useState("Nakit Ana Kasa");
  const [expDate, setExpDate] = useState(new Date().toISOString().slice(0, 10));

  // Quick Payment form
  const [payStudentId, setPayStudentId] = useState("");
  const [payAmount, setPayAmount] = useState("");
  const [payMethod, setPayMethod] = useState("Nakit");
  const [payBank, setPayBank] = useState("T.C. VAKIFLAR BANKASI İNEGÖL ŞB.");
  const [payNote, setPayNote] = useState("Taksit Tahsilatı");
  const [payDate, setPayDate] = useState(new Date().toISOString().slice(0, 10));
  const [payLoading, setPayLoading] = useState(false);

  const [periodFilter, setPeriodFilter] = useState("2026-2027"); // '2026-2027' (Edesis Standart) | 'all'
  const [accountingVersion, setAccountingVersion] = useState(0);

  useEffect(() => {
    const handleUpdate = () => setAccountingVersion((v) => v + 1);
    window.addEventListener("cozum_snapshot_updated", handleUpdate);
    window.addEventListener("cozum_accounting_updated", handleUpdate);
    return () => {window.removeEventListener("cozum_snapshot_updated",handleUpdate);window.removeEventListener("cozum_accounting_updated",handleUpdate);};
  }, []);

  const allStudents = useMemo(() => data.students || [], [data.students]);
  const students = useMemo(() => {
    if (periodFilter === "2026-2027") {
      return allStudents.filter(s => getStudentCategory(s) === "kesin");
    }
    if (periodFilter === "2026-2027-YAZ") {
      return allStudents.filter(s => getStudentCategory(s) === "yaz");
    }
    if (periodFilter === "2025-2026") {
      return allStudents.filter(s => getStudentCategory(s) === "mezun");
    }
    return allStudents;
  }, [allStudents, periodFilter]);

  const payments = useMemo(() => (data.payments || []).filter(p => !p.is_cancelled && !["İptal Edildi", "cancelled"].includes(p.status)), [data.payments]);
  const [expenses, setExpenses] = useState(data.expenses || []);

  useEffect(() => {
    setExpenses(data.expenses || []);
  }, [data.expenses]);

  const todayStr = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const currentMonthStr = useMemo(() => new Date().toISOString().slice(0, 7), []);

  // 1. ÖĞRENCİ GERÇEK TAKSİT PLANLARI HARİTASI (Edesis 664 Taksit Birebir Entegrasyonu)
  const studentPlansMap = useMemo(() => {
    void accountingVersion;
    const bySozlesme = {};
    const rawTaksitler = AccountingService.getInstallments() || [];

    rawTaksitler.forEach((t) => {
      if (t.sozlesmeNo) {
        if (!bySozlesme[t.sozlesmeNo]) bySozlesme[t.sozlesmeNo] = [];
        bySozlesme[t.sozlesmeNo].push(t);
      }
    });

    const map = {};
    students.forEach((s) => {
      const rawList = (s.sozlesme_no ? bySozlesme[s.sozlesme_no] : null) || [];
      if (rawList.length > 0) {
        const sorted = [...rawList].sort((a, b) => (Number(a.taksitNo) || 0) - (Number(b.taksitNo) || 0));
        map[s.id] = sorted.map((t, idx) => {
          const amt = Number(t.taksitTutari) || 0;
          const paid = Number(t.tahsilEdilen) || 0;
          const remaining = t.kalanTutar != null ? Number(t.kalanTutar) : Math.max(0, amt - paid);
          const status = remaining <= 0 ? "paid" : (paid > 0 ? "partial" : "pending");
          const dueStr = t.vadeTarihi ? String(t.vadeTarihi).slice(0, 10) : "";
          return {
            no: Number(t.taksitNo) || (idx + 1),
            due_date: dueStr,
            amount: amt,
            paid: paid,
            remaining: remaining,
            status: status,
            taksitId: t.id,
            gelirKodu: t.gelirKodu,
            durumText: t.durumText
          };
        });
      } else {
        map[s.id] = [];
      }
    });
    return map;
  }, [students, accountingVersion]);

  // 2. ÜST FİNANSAL KPI HESAPLAMALARI (Edesis Muhasebe V2 Birebir Parite + Canlı Senkronizasyon)
  const totalContractRevenue = useMemo(() => {

    if (Array.isArray(data.contracts)) return data.contracts.filter(c=>!c.is_deleted && c.durum!==3).reduce((sum,c)=>sum+Number(c.brut_tutar ?? c.brutTutar ?? 0),0);
    return allStudents.reduce((sum,s)=>sum+getEffectiveTotalFee(s),0);
  }, [data.contracts, allStudents]);

  const totalCollected = useMemo(() => {

    return payments.filter(p => !p.is_cancelled && p.status !== "İptal Edildi").reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
  }, [payments]);

  const totalExpenses = useMemo(() => {

    return expenses.reduce((sum, e) => sum + (Number(e.amount) || 0), 0);
  }, [expenses]);

  const totalRemaining = useMemo(() => {
    if (Array.isArray(data.contracts)) return data.contracts.filter(c=>!c.is_deleted && c.durum!==3).reduce((sum,c)=>sum+Math.max(0,Number(c.kalan_alacak ?? c.kalanAlacak ?? (Number(c.brut_tutar ?? c.brutTutar ?? 0)-Number(c.toplam_tahsilat ?? c.toplamTahsilat ?? c.calculated_paid ?? 0)))),0);
    return Math.max(0, totalContractRevenue - totalCollected);
  }, [data.contracts, totalContractRevenue, totalCollected]);

  const collectionRate = useMemo(() => {
    return totalContractRevenue > 0 ? Math.round(((totalContractRevenue-totalRemaining) / totalContractRevenue) * 100) : 0;
  }, [totalContractRevenue, totalRemaining]);

  const netCashBalance = useMemo(() => {
    return totalCollected - totalExpenses;
  }, [totalCollected, totalExpenses]);

  const profitMarginFormatted = useMemo(() => {

    if (totalContractRevenue <= 0) return "0";
    const net = totalCollected - totalExpenses;
    return String(Math.round((net / totalContractRevenue) * 100));
  }, [totalContractRevenue, totalCollected, totalExpenses]);

  // GAP-1: Bu Ay Vadesi Gelenler (Cari Ay Taksit Tahakkuku)
  const dueThisMonthData = useMemo(() => {

    let total = 0;
    let count = 0;
    let remaining = 0;
    students.forEach((s) => {
      const plan = studentPlansMap[s.id] || [];
      plan.forEach((inst) => {
        if (inst.due_date && inst.due_date.startsWith(currentMonthStr)) {
          total += inst.amount;
          count += 1;
          remaining += inst.remaining;
        }
      });
    });
    return {
      total,
      count,
      remaining,
      collected: Math.max(0, total - remaining)
    };
  }, [students, studentPlansMap, currentMonthStr]);

  // GAP-5: Taksit Yaşlandırma & Gecikme Takibi (Gerçek Vade Tarihine Göre)
  const overdueStudents = useMemo(() => {
    return students
      .map((s) => {
        const total = getEffectiveTotalFee(s);
        const paid = Number(s.paid_amount) || 0;
        const remaining = Math.max(0, total - paid);
        if (remaining <= 0) return null;

        const plan = studentPlansMap[s.id] || [];
        const overdueInsts = plan.filter(inst => inst.remaining > 0 && inst.due_date && inst.due_date < todayStr);

        let daysOverdue = 0;
        let overdueDebt = remaining;

        if (overdueInsts.length > 0) {
          const earliestDue = overdueInsts.map(i => i.due_date).sort()[0];
          daysOverdue = Math.floor((new Date(todayStr).getTime() - new Date(earliestDue).getTime()) / 86400000);
          overdueDebt = overdueInsts.reduce((a, b) => a + b.remaining, 0);
        } else if (s.due_date) {
          const diff = Math.floor((new Date(todayStr).getTime() - new Date(s.due_date).getTime()) / 86400000);
          if (diff <= 0) return null;
          daysOverdue = diff;
        } else {
          return null;
        }

        if (daysOverdue <= 0) return null;

        let agingCategory = "1-30";
        if (daysOverdue > 60) agingCategory = "60+";
        else if (daysOverdue > 30) agingCategory = "31-60";

        return {
          ...s,
          remaining: overdueDebt,
          daysOverdue,
          agingCategory,
          overdueCount: overdueInsts.length || 1,
          earliestDueDate: overdueInsts[0]?.due_date || s.due_date
        };
      })
      .filter(Boolean);
  }, [students, studentPlansMap, todayStr]);

  const totalOverdueDebt = useMemo(() => {

    return overdueStudents.reduce((sum, s) => sum + s.remaining, 0);
  }, [overdueStudents]);

  const agingSummary = useMemo(() => {


    const c1_30 = overdueStudents.filter(s => s.agingCategory === "1-30");
    const c31_60 = overdueStudents.filter(s => s.agingCategory === "31-60");
    const c60plus = overdueStudents.filter(s => s.agingCategory === "60+");

    const sum1_30 = c1_30.reduce((a, s) => a + s.remaining, 0);
    const sum31_60 = c31_60.reduce((a, s) => a + s.remaining, 0);
    const sum60plus = c60plus.reduce((a, s) => a + s.remaining, 0);

    const pct1_30 = totalOverdueDebt > 0 ? Math.round((sum1_30 / totalOverdueDebt) * 100) : 0;
    const pct31_60 = totalOverdueDebt > 0 ? Math.round((sum31_60 / totalOverdueDebt) * 100) : 0;
    const pct60plus = totalOverdueDebt > 0 ? Math.max(0, 100 - pct1_30 - pct31_60) : 0;

    return {
      c1_30: { count: c1_30.length, sum: sum1_30, pct: pct1_30 },
      c31_60: { count: c31_60.length, sum: sum31_60, pct: pct31_60 },
      c60plus: { count: c60plus.length, sum: sum60plus, pct: pct60plus }
    };
  }, [overdueStudents, totalOverdueDebt]);

  // 3. KASA & BANKA HESAP BAKİYELERİ (Edesis Canlı 975.438 ₺ Tam Parite)
  const registers = useMemo(() => {


    let vakifbankInflow = 0;
    let halkbankInflow = 0;
    let nakitInflow = 0;
    let posInflow = 0;
    let senetInflow = 0;

    payments.forEach((p) => {
      const m = (p.method || "").toLowerCase();
      const b = (p.bank || "").toLowerCase();
      const amt = Number(p.amount || 0);

      if (b.includes("vakıf") || b.includes("vakif") || m.includes("vakıf") || m.includes("vakif")) {
        vakifbankInflow += amt;
      } else if (b.includes("halk") || m.includes("halk")) {
        halkbankInflow += amt;
      } else if (m.includes("kart") || m.includes("pos")) {
        posInflow += amt;
      } else if (m.includes("senet") || m.includes("çek") || m.includes("cek")) {
        senetInflow += amt;
      } else if (m.includes("nakit") || m.includes("elden") || m.includes("kasa")) {
        nakitInflow += amt;
      } else {
        if (m.includes("havale") || m.includes("eft") || m.includes("bank")) {
          vakifbankInflow += amt;
        } else {
          nakitInflow += amt;
        }
      }
    });

    let vakifbankExpense = 0;
    let halkbankExpense = 0;
    let nakitExpense = 0;
    let posExpense = 0;

    expenses.forEach((e) => {
      const acc = (e.account || "").toLowerCase();
      const amt = Number(e.amount || 0);
      if (acc.includes("vakıf") || acc.includes("vakif")) {
        vakifbankExpense += amt;
      } else if (acc.includes("halk")) {
        halkbankExpense += amt;
      } else if (acc.includes("pos") || acc.includes("kart")) {
        posExpense += amt;
      } else {
        nakitExpense += amt;
      }
    });

    return {
      vakifbankInflow,
      vakifbankExpense,
      vakifbank: vakifbankInflow - vakifbankExpense,

      halkbankInflow,
      halkbankExpense,
      halkbank: halkbankInflow - halkbankExpense,

      nakitInflow,
      nakitExpense,
      nakit: nakitInflow - nakitExpense,

      posInflow,
      posExpense,
      pos: posInflow - posExpense,

      senetInflow,
      senet: senetInflow,

      grossNakit: nakitInflow
    };
  }, [payments, expenses]);

  const netLiquidBalance = registers.vakifbank + registers.halkbank + registers.pos + registers.nakit;

  // 4. 12 AYLIK DİNAMİK GELİR & TAHSİLAT TREND GRAFİĞİ (GAP-4)
  const monthlyTrends = useMemo(() => {
    void accountingVersion;
    const MONTH_NAMES = [
      "Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
      "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"
    ];
    const currentYear = new Date().getFullYear();



    return MONTH_NAMES.map((name, idx) => {
      const monthNum = String(idx + 1).padStart(2, "0");
      const monthPrefix = `${currentYear}-${monthNum}`;

      let plan = 0;
      students.forEach((s) => {
        const studentPlan = studentPlansMap[s.id] || [];
        studentPlan.forEach((inst) => {
          if (inst.due_date && inst.due_date.startsWith(monthPrefix)) {
            plan += inst.amount;
          }
        });
      });

      let gerceklesen = 0;
      payments.forEach((p) => {
        if (p.payment_date && p.payment_date.startsWith(monthPrefix)) {
          gerceklesen += Number(p.amount || 0);
        }
      });

      const uyum = plan > 0 ? Math.min(100, Math.round((gerceklesen / plan) * 100)) : (gerceklesen > 0 ? 100 : 0);

      return {
        ay: name,
        ayKod: monthPrefix,
        plan: Math.round(plan),
        planlanan: Math.round(plan),
        gerceklesen: Math.round(gerceklesen),
        uyum
      };
    });
  }, [students, studentPlansMap, payments, accountingVersion]);

  // 5. GELİR KATEGORİLERİ & TAHSİLAT YÖNTEMLERİ DAĞILIMI (GAP-6)
  const incomeCategoryData = useMemo(() => {
    void accountingVersion;
    const allIncomes = AccountingService.getIncomes();


    let sozlesme = 0;
    let kitap = 0;
    let dijital = 0;
    let dnm = 0;
    let ozelDers = 0;

    students.forEach((s) => {
      const base = Math.max(0, (Number(s.total_fee) || 0) - (Number(s.discount_amount) || 0));
      sozlesme += base;

      let extras = {};
      try {
        extras = typeof s.extra_fees === "string" ? JSON.parse(s.extra_fees || "{}") : (s.extra_fees || {});
      } catch {}
      kitap += Number(extras.kaynak || 0);
      dijital += Number(extras.dijital || 0);
      dnm += Number(extras.dnm || 0);
      ozelDers += Number(extras.ozel_ders || 0) + Number(extras.kutuphane || 0);
    });

    const total = sozlesme + kitap + dijital + dnm + ozelDers || 1;
    return [
      { name: "Sözleşmeler", value: sozlesme, color: "#2563eb", pct: Math.round((sozlesme / total) * 100) },
      { name: "Yayın & Kitap", value: kitap, color: "#16a34a", pct: Math.round((kitap / total) * 100) },
      { name: "Dijital Platform", value: dijital, color: "#ca8a04", pct: Math.round((dijital / total) * 100) },
      { name: "Deneme Kulübü", value: dnm, color: "#9333ea", pct: Math.round((dnm / total) * 100) },
      { name: "Etüt & Özel Ders", value: ozelDers, color: "#ea580c", pct: Math.round((ozelDers / total) * 100) },
    ].filter(item => item.value > 0);
  }, [students, accountingVersion]);

  const paymentMethodData = useMemo(() => {
    const allCollections = AccountingService.getCollections(payments);


    const total = totalCollected || 1;
    return [
      { name: "Nakit Ana Kasa", value: registers.nakitInflow, color: "#ca8a04", pct: Math.round((registers.nakitInflow / total) * 100) },
      { name: "POS / Kredi Kartı", value: registers.posInflow, color: "#db2777", pct: Math.round((registers.posInflow / total) * 100) },
      { name: "VakıfBank Havale", value: registers.vakifbankInflow, color: "#16a34a", pct: Math.round((registers.vakifbankInflow / total) * 100) },
      { name: "Halkbank Havale", value: registers.halkbankInflow, color: "#2563eb", pct: Math.round((registers.halkbankInflow / total) * 100) },
      { name: "Senet / Bono", value: registers.senetInflow, color: "#7c3aed", pct: Math.round((registers.senetInflow / total) * 100) },
    ].filter(item => item.value > 0);
  }, [registers, totalCollected, payments]);

  // Filtered Payments List (Kasalar Tab & Tahsilatlar Tab)
  const filteredPaymentsList = useMemo(() => {
    let running = 0;
    return payments
      .filter((p) => {
        const matchSearch = trIncludes(`${p.student_name || ""} ${p.method || ""} ${p.bank || ""} ${p.note || ""}`, searchQuery);
        if (!matchSearch) return false;

        const m = (p.method || "").toLowerCase();
        const b = (p.bank || "").toLowerCase();
        if (selectedRegisterFilter === "vakif") return m.includes("vakıf") || m.includes("vakif") || b.includes("vakıf") || b.includes("vakif") || m.includes("havale") || m.includes("eft");
        if (selectedRegisterFilter === "halk") return m.includes("halk") || b.includes("halk");
        if (selectedRegisterFilter === "nakit") return m.includes("nakit") || m.includes("elden");
        if (selectedRegisterFilter === "pos") return m.includes("kart") || m.includes("pos");
        if (selectedRegisterFilter === "senet") return m.includes("senet") || m.includes("çek");

        return true;
      })
      .map((p) => {
        running += Number(p.amount || 0);
        return {
          ...p,
          runningBalance: running
        };
      });
  }, [payments, searchQuery, selectedRegisterFilter]);

  const studentNameMap = useMemo(() => {
    const map = new Map();
    (allStudents || []).forEach((s) => {
      if (s.id) map.set(String(s.id), s.student_name);
      if (s.student_no) map.set(String(s.student_no), s.student_name);
    });
    return map;
  }, [allStudents]);

  // Çift Yönlü Kasa & Banka Defteri (Tahsilatlar + Giderler)
  const allLedgerItems = useMemo(() => {
    const list = [];
    payments.forEach((p) => {
      const info = getPaymentAccountInfo(p);
      const studentName = p.student_name || studentNameMap.get(String(p.student_id)) || "Öğrenci Kaydı";
      list.push({
        id: p.id,
        isInflow: true,
        type: "tahsilat",
        date: p.payment_date || "2026-10-02",
        accountKey: info.key,
        accountLabel: info.label,
        party: studentName,
        description: p.note || "Taksit Ödemesi",
        amount: Number(p.amount || 0),
        rawPayment: p,
      });
    });

    expenses.forEach((e) => {
      const info = getExpenseAccountInfo(e);
      list.push({
        id: e.id,
        isInflow: false,
        type: "gider",
        date: e.expense_date || "2026-10-02",
        accountKey: info.key,
        accountLabel: info.label,
        party: e.category ? `Gider: ${e.category}` : "Kurum Gideri",
        description: e.title || "İşletme Gideri",
        amount: Number(e.amount || 0),
        rawExpense: e,
      });
    });

    return list.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }, [payments, expenses, studentNameMap]);

  const filteredLedgerList = useMemo(() => {
    let running = 0;
    return allLedgerItems
      .filter((item) => {
        const matchSearch = trIncludes(`${item.party || ""} ${item.accountLabel || ""} ${item.description || ""}`, searchQuery);
        if (!matchSearch) return false;

        if (selectedRegisterFilter !== "all" && item.accountKey !== selectedRegisterFilter) {
          return false;
        }
        return true;
      })
      .map((item) => {
        if (item.isInflow) {
          running += item.amount;
        } else {
          running = Math.max(0, running - item.amount);
        }
        return {
          ...item,
          runningBalance: running,
        };
      });
  }, [allLedgerItems, searchQuery, selectedRegisterFilter]);

  // Filtered Overdue Students
  const filteredOverdueList = useMemo(() => {
    return overdueStudents.filter((s) => {
      const matchSearch = trIncludes(`${s.student_name} ${s.student_no} ${s.parent_name} ${s.parent_phone}`, searchQuery);
      if (!matchSearch) return false;

      if (agingFilter !== "all" && s.agingCategory !== agingFilter) return false;

      return true;
    });
  }, [overdueStudents, searchQuery, agingFilter]);

  // WhatsApp Borç Hatırlatma Gönder (GAP-5: öğrenci adı, vade tarihi, kalan borç, VakıfBank IBAN)
  const sendWhatsAppReminder = (student) => {
    const phone = (student.parent_phone || "").replace(/[^0-9]/g, "");
    let cleanPhone = phone.startsWith("0") ? "9" + phone : (phone.length === 10 ? "90" + phone : phone);
    const dueDateStr = student.due_date ? ` (${new Date(student.due_date).toLocaleDateString("tr-TR")} vadeli)` : "";
    const msg = `Sayın ${student.parent_name || "Velimiz"},\nÖzel İnegöl Çözüm Kurs Merkezi olarak ${student.student_name} isimli öğrencimizin${dueDateStr} vadesi gelen ₺${student.remaining.toLocaleString("tr-TR")} tutarındaki taksit ödemesini hatırlatmak isteriz.\nÖdeme İşlemleri için:\nT.C. VAKIFLAR BANKASI İNEGÖL ŞUBESİ\nIBAN: TR04 0001 5001 5800 7354 4922 43 (Nevin Çakmak)\nİletişim: 0224 715 00 02`;
    window.open(`https://wa.me/${cleanPhone}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  // Toplu WhatsApp Hatırlatma
  const handleBulkWhatsAppReminder = () => {
    if (overdueStudents.length === 0) {
      alert("Vadesi geçmiş taksiti olan öğrenci bulunmuyor.");
      return;
    }
    const target = overdueStudents[0];
    sendWhatsAppReminder(target);
    alert(`Toplam ${overdueStudents.length} geciken taksit tespit edildi. İlk veli (${target.parent_name} - ${target.student_name}) için WhatsApp mesajı açıldı.`);
  };

  // GAP-3: Gider Kaydetme (Kasa/Banka Seçimi Dahil)
  const handleSaveExpense = async (e) => {
    e.preventDefault();
    if (!expTitle || !expAmount || expenseSaving.current) return;
    expenseSaving.current=true;setSavingExpense(true);
    try {
      const amt = parseFloat(expAmount);
      const res = await axios.post(`${API}/expenses`, {
        id: expenseId.current,
        title: expTitle,
        amount: amt,
        category: expCategory,
        account: expAccount,
        expense_date: expDate
      });
      const newExp = {
        id: res.data?.id || `exp-${Date.now()}`,
        title: expTitle,
        amount: amt,
        category: expCategory,
        account: expAccount,
        expense_date: expDate
      };
      setExpenses(previous => [newExp, ...previous.filter(item => item.id !== newExp.id)]);
      setShowExpenseModal(false);
      setExpTitle("");
      setExpAmount("");
      setExpAccount("Nakit Ana Kasa");
      alert("Kurum gideri başarıyla kaydedildi.");
      if (reload) reload();
    } catch (err) {
      alert("Gider kaydedilemedi: " + (err.response?.data?.detail || err.message));
    } finally {
      expenseSaving.current=false;setSavingExpense(false);
    }
  };

  // Hızlı Tahsilat Kaydetme
  const handleSavePayment = async (e) => {
    e.preventDefault();
    if (!payStudentId || !payAmount) {
      alert("Lütfen öğrenci ve tahsilat tutarını seçiniz.");
      return;
    }
    setPayLoading(true);
    try {
      const amt = parseFloat(payAmount);
      const studentObj = students.find(s => s.id === payStudentId);
      const res = await axios.post(`${API}/payments`, {
        id:paymentId.current,
        student_id: payStudentId,
        sozlesme_no: studentObj?.sozlesme_no,
        amount: amt,
        method: payMethod,
        bank: payMethod === "Banka Havale / EFT" ? payBank : "",
        payment_date: payDate,
        note: payNote
      });
      await AccountingService.recordCollection({
        studentId: payStudentId,
        studentName: studentObj?.student_name,
        amount: amt,
        method: payMethod,
        bank: payMethod === "Banka Havale / EFT" ? payBank : "Nakit Ana Kasa",
        note: payNote,
        gelirKodu: studentObj?.gelir_kodu,
        sozlesmeNo: studentObj?.sozlesme_no,
        alreadyPersisted: true
      });
      setShowQuickPaymentModal(false);

      setReceiptPayment({
        id: res.data?.id || `pmt-${Date.now()}`,
        student_id: payStudentId,
        student_name: studentObj?.student_name || "Öğrenci",
        parent_name: studentObj?.parent_name || "Veli",
        amount: amt,
        method: payMethod,
        payment_date: payDate,
        note: payNote
      });

      setPayAmount("");
      setPayStudentId("");
      alert("Tahsilat başarıyla kaydedildi! Resmi tahsilat makbuzu ekrana getiriliyor...");
      if (reload) reload();
    } catch (err) {
      alert("Tahsilat kaydedilemedi: " + (err.response?.data?.detail || err.message));
    } finally {
      setPayLoading(false);
    }
  };

  return (
    <div className="muhasebe-v2-container" style={{ display: "flex", flexDirection: "column", gap: "20px" }}>

      {/* 1. ÜST BAŞLIK VE HIZLI EYLEM DÜĞMELERİ */}
      <div className="page-head" style={{ marginBottom: "4px" }}>
        <div>
          <div className="eyebrow" style={{ color: "#2563eb", fontWeight: "700", display: "flex", alignItems: "center", gap: "6px" }}>
            <ShieldCheck size={16} /> ÖZEL İNEGÖL ÇÖZÜM KURSU · EDESİS MUHASEBE V2
          </div>
          <h1 style={{ fontSize: "24px", fontWeight: "800", color: "#0f172a", margin: "4px 0" }}>
            Muhasebe & Finans Yönetim Portali
          </h1>
          <p style={{ color: "#64748b", margin: 0, fontSize: "13px" }}>
            Kurum gelirleri, kasa/banka çift yönlü defteri, taksit yaşlandırma ve resmi makbuz/senet motoru.
          </p>
          <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap", marginTop: "8px" }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: "6px", background: "#f8fafc", border: "1px solid var(--border)", borderRadius: "8px", padding: "3px 8px" }}>
              <span style={{ fontSize: "12px", fontWeight: "700", color: "#475569", display: "inline-flex", alignItems: "center", gap: "4px" }}>
                <Calendar size={13} color="#2563eb" /> Dönem:
              </span>
              <select
                value={periodFilter}
                onChange={(e) => setPeriodFilter(e.target.value)}
                style={{
                  fontSize: "12px",
                  fontWeight: 700,
                  border: "none",
                  background: "transparent",
                  color: "#0f172a",
                  cursor: "pointer",
                  outline: "none"
                }}
              >
                <option value="2026-2027">2026-2027 (Edesis Standart)</option>
                <option value="2026-2027-YAZ">2026-2027-YAZ (Yaz Kursu)</option>
                <option value="2025-2026">2025-2026 (Önceki Dönem)</option>
                <option value="all">Tüm Dönemler</option>
              </select>
            </div>

            <button
              type="button"
              onClick={() => setPeriodFilter("2026-2027")}
              style={{
                padding: "5px 12px",
                fontSize: "12px",
                fontWeight: 700,
                borderRadius: "8px",
                border: periodFilter === "2026-2027" ? "2px solid #16a34a" : "1px solid var(--border)",
                background: periodFilter === "2026-2027" ? "#f0fdf4" : "#fff",
                color: periodFilter === "2026-2027" ? "#15803d" : "#475569",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: "5px"
              }}
            >
              <CheckCircle2 size={14} /> 🟢 2026-2027 Kesin Kayıt
            </button>
            <button
              type="button"
              onClick={() => setPeriodFilter("2026-2027-YAZ")}
              style={{
                padding: "5px 12px",
                fontSize: "12px",
                fontWeight: 700,
                borderRadius: "8px",
                border: periodFilter === "2026-2027-YAZ" ? "2px solid #f59e0b" : "1px solid var(--border)",
                background: periodFilter === "2026-2027-YAZ" ? "#fef3c7" : "#fff",
                color: periodFilter === "2026-2027-YAZ" ? "#b45309" : "#475569",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: "5px"
              }}
            >
              ☀️ Yaz Kursu (2026-2027-YAZ)
            </button>
            <button
              type="button"
              onClick={() => setPeriodFilter("all")}
              style={{
                padding: "5px 12px",
                fontSize: "12px",
                fontWeight: 700,
                borderRadius: "8px",
                border: periodFilter === "all" ? "2px solid #2563eb" : "1px solid var(--border)",
                background: periodFilter === "all" ? "#eff6ff" : "#fff",
                color: periodFilter === "all" ? "#1d4ed8" : "#475569",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: "5px"
              }}
            >
              <Building size={14} /> 📁 Tüm Dönemler ({allStudents.length} Öğrenci)
            </button>
          </div>
        </div>

        <div className="head-actions" style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          <button
            className="btn"
            onClick={() => setShowQuickPaymentModal(true)}
            style={{ display: "inline-flex", alignItems: "center", gap: "6px", background: "#16a34a", color: "#fff", fontWeight: "700" }}
          >
            <Plus size={16} /> Hızlı Tahsilat Al (Makbuzlu)
          </button>
          <button
            className="btn secondary"
            onClick={() => setShowExpenseModal(true)}
            style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
          >
            <TrendingDown size={16} color="#ef4444" /> Gider Ekle
          </button>
          <button
            className="btn secondary"
            onClick={handleBulkWhatsAppReminder}
            style={{ display: "inline-flex", alignItems: "center", gap: "6px", borderColor: "#22c55e", color: "#16a34a" }}
            title="Vadesi geçmiş öğrencilerin velilerine tek tıkla WhatsApp borç bildirimi gönderir"
          >
            <MessageCircle size={16} /> Gecikenlere WhatsApp Hatırlat
          </button>
          <button
            className="btn secondary"
            onClick={() => onNavigate && onNavigate("documents")}
            style={{ display: "inline-flex", alignItems: "center", gap: "6px", borderColor: "#3b82f6", color: "#2563eb", fontWeight: "600" }}
            title="Edesis Resmi Sözleşmeler Sayfası"
          >
            <FileText size={16} /> 📄 Edesis Sözleşmeler
          </button>
          {ENABLE_KASA_BANKA && (
            <button
              className="btn secondary"
              onClick={() => window.print()}
              style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
            >
              <Printer size={16} /> Kasa Raporu Yazdır
            </button>
          )}
        </div>
      </div>

      {/* 2. MASTER FİNANSAL KPI GÖSTERGE KARTLARI */}
      {["gecikenler", "vade_takvimi"].includes(activeTab) && <>
      <MuhasebeKpiGrid
        fmtMoney={fmtMoney}
        totalContractRevenue={totalContractRevenue}
        students={students}
        totalCollected={totalCollected}
        collectionRate={collectionRate}
        dueThisMonthData={dueThisMonthData}
        totalOverdueDebt={totalOverdueDebt}
        overdueStudents={overdueStudents}
        totalRemaining={totalRemaining}
        netLiquidBalance={netLiquidBalance}
      />
      <p style={{color:"var(--muted)",fontSize:12}}>Ciro ve kasa özeti tüm dönemleri kapsar. Dönem filtresi öğrenci ve vade listelerine uygulanır.</p>
      </>}

      {/* 3. KASA & BANKA PORTFÖY ŞERİDİ (5 Edesis Hesabı) */}
      {ENABLE_KASA_BANKA && (
        <BankPortfolioStrip
          registers={registers}
          fmtMoney={fmtMoney}
          setActiveTab={setActiveTab}
          setSelectedRegisterFilter={setSelectedRegisterFilter}
        />
      )}

      {/* MODÜL SEKMELERİ (KULLANICI TALEBİYLE KALDIRILDI - SOL MENÜ KULLANILIYOR) */}
      <div className="muhasebe-tabs-scroll" style={{ display: "none" }}>
        <button
          className={`btn ${activeTab === "dashboard" ? "" : "secondary"}`}
          onClick={() => setActiveTab("dashboard")}
          style={{ fontWeight: "700", display: "inline-flex", alignItems: "center", gap: "6px", background: activeTab === "dashboard" ? "#2563eb" : undefined, color: activeTab === "dashboard" ? "#fff" : undefined }}
        >
          <Zap size={16} /> ⚡ Kontrol Paneli (Bugün)
        </button>
        <button
          className={`btn ${activeTab === "gelirler" ? "" : "secondary"}`}
          onClick={() => setActiveTab("gelirler")}
          style={{ fontWeight: "700", display: "inline-flex", alignItems: "center", gap: "6px" }}
        >
          <FileText size={16} /> 📑 Gelir Yönetimi (Gelir Listesi) ({AccountingService.getIncomes()?.length || 0})
        </button>
        <button
          className={`btn ${activeTab === "gecikenler" ? "" : "secondary"}`}
          onClick={() => setActiveTab("gecikenler")}
          style={{ fontWeight: "700", display: "inline-flex", alignItems: "center", gap: "6px", color: activeTab === "gecikenler" ? "#fff" : "#dc2626" }}
        >
          <AlertTriangle size={16} /> ⚠️ Geciken Taksitler & Yaşlandırma ({overdueStudents.length})
        </button>
        <button
          className={`btn ${activeTab === "vade_takvimi" ? "" : "secondary"}`}
          onClick={() => setActiveTab("vade_takvimi")}
          style={{ fontWeight: "600", display: "inline-flex", alignItems: "center", gap: "6px" }}
        >
          <Calendar size={16} /> 📅 Vade Takvimi ({AccountingService.getInstallments()?.length || 0})
        </button>
        {ENABLE_KASA_BANKA && (
          <button
            className={`btn ${activeTab === "kasalar" ? "" : "secondary"}`}
            onClick={() => setActiveTab("kasalar")}
            style={{ fontWeight: "600", display: "inline-flex", alignItems: "center", gap: "6px" }}
          >
            <Building size={16} /> 💳 Kasa & Banka Defteri
          </button>
        )}
        <button
          className={`btn ${activeTab === "tahsilatlar" ? "" : "secondary"}`}
          onClick={() => setActiveTab("tahsilatlar")}
          style={{ fontWeight: "600", display: "inline-flex", alignItems: "center", gap: "6px" }}
        >
          <Receipt size={16} /> 🧾 Tahsilat Listesi ({AccountingService.getCollections(payments)?.length || 0})
        </button>
        <button
          className={`btn ${activeTab === "giderler" ? "" : "secondary"}`}
          onClick={() => setActiveTab("giderler")}
          style={{ fontWeight: "600", display: "inline-flex", alignItems: "center", gap: "6px" }}
        >
          <TrendingDown size={16} /> 📉 Kurum Giderleri ({expenses.length})
        </button>
        <button
          className={`btn ${activeTab === "senetler" ? "" : "secondary"}`}
          onClick={() => setActiveTab("senetler")}
          style={{ fontWeight: "600", display: "inline-flex", alignItems: "center", gap: "6px" }}
        >
          <FileText size={16} /> 📜 Matbu Senet (Bono)
        </button>
        <button
          className={`btn ${activeTab === "backup" ? "" : "secondary"}`}
          onClick={() => setActiveTab("backup")}
          style={{ fontWeight: "700", display: "inline-flex", alignItems: "center", gap: "6px", color: activeTab === "backup" ? "#fff" : "#16a34a", borderColor: "#16a34a", background: activeTab === "backup" ? "#16a34a" : "#f0fdf4" }}
        >
          <ShieldCheck size={16} /> 💾 Otomatik Yedekleme (20:00)
        </button>
      </div>

      {/* 4. SEKME İÇERİKLERİ */}
      {/* SEKME 1: EDESİS KONTROL PANELİ & DASHBOARD */}
      {activeTab === "dashboard" && (
        <OverviewTab
          onOpen={onOpen}
          onSelectStudent={onSelectStudent}
          onNavigateTab={(tab) => {
            if (onTabChange) onTabChange(tab);
            else setInternalTab(tab);
          }}
          onRefresh={reload}
          monthlyTrends={monthlyTrends}
          agingSummary={agingSummary}
          incomeCategoryData={incomeCategoryData}
          paymentMethodData={paymentMethodData}
          totalExpenses={totalExpenses} payments={payments} expenses={expenses} netLiquidBalance={netLiquidBalance} installmentPlans={Object.values(studentPlansMap).flat()}
        totalCollected={totalCollected}
          totalContractRevenue={totalContractRevenue}
          totalRemaining={totalRemaining}
          collectionRate={collectionRate}
          totalOverdueDebt={totalOverdueDebt}
          overdueStudents={overdueStudents}
          handleBulkWhatsAppReminder={handleBulkWhatsAppReminder}
          fmtMoney={fmtMoney}
        />
      )}

      {/* SEKME 2: KASA & BANKA DEFTERİ (GAP-2: Tahsilat / Kasa Hareketleri & Bakiye) */}
      {ENABLE_KASA_BANKA && activeTab === "kasalar" && (
        <CashBankLedgerTab
          registers={registers}
          fmtMoney={fmtMoney}
          selectedRegisterFilter={selectedRegisterFilter}
          setSelectedRegisterFilter={setSelectedRegisterFilter}
          allLedgerItems={allLedgerItems}
          filteredLedgerList={filteredLedgerList}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          setReceiptPayment={setReceiptPayment}
        />
      )}

      {/* SEKME 3: GECİKEN TAKSİTLER & YAŞLANDIRMA (WHATSAPP HATIRLATMA) */}
      {activeTab === "gecikenler" && (
        <OverdueAgingTab
          overdueStudents={overdueStudents}
          agingSummary={agingSummary}
          onSelectStudent={onSelectStudent}
          sendWhatsAppReminder={sendWhatsAppReminder}
          onQuickPayment={(s) => {
            setPayStudentId(s.id);
            setPayAmount(String(s.remaining));
            setShowQuickPaymentModal(true);
          }}
          fmtMoney={fmtMoney}
        />
      )}

      {/* SEKME 3B: EDESİS VADE TAKVİMİ (688 GERÇEK TAKSİT & YAŞLANDIRMA) */}
      {activeTab === "vade_takvimi" && (
        <ScheduleCalendarTab
          students={students}
          onSelectStudent={onSelectStudent}
          onOpenGelir={handleOpenUnifiedContract}
          onNavigate={onNavigate}
        />
      )}

      {/* SEKME 4: EDESİS TAHSİLAT LİSTESİ (159 GERÇEK TAHSİLAT & MAKBUZLAR) */}
      {activeTab === "tahsilatlar" && (
        <CollectionsTab
          students={students}
          payments={payments}
          API={API}
          onSelectStudent={onSelectStudent}
          onOpenGelir={handleOpenUnifiedContract}
          onNavigate={onNavigate}
        />
      )}

      {/* SEKME 5: KURUM GİDERLERİ */}
      {activeTab === "giderler" && (
        <ExpensesTab
          expenses={expenses}
          onAddExpense={() => setShowExpenseModal(true)}
          fmtMoney={fmtMoney}
        />
      )}

      {/* SEKME 6: MATBU SENET (BONO) SÜİTİ */}
      {activeTab === "senetler" && (
        <SenetlerTab
          students={students}
          fmtMoney={fmtMoney}
          getEffectiveTotalFee={getEffectiveTotalFee}
          setPromissoryStudent={setPromissoryStudent}
        />
      )}

      {/* SEKME 7: EDESİS GELİRLER & SÖZLEŞMELER */}
      {activeTab === "gelirler" && (
        <IncomesTab
          students={students}
          onSelectStudent={onSelectStudent}
          onOpenGelir={handleOpenUnifiedContract}
          onNavigate={onNavigate}
        />
      )}

      {/* SEKME 8: 20:00 OTOMATİK YEDEKLEME & GERİ YÜKLEME (1-CLICK SNAPSHOT & RESTORE) */}
      {activeTab === "backup" && (
        <BackupRestoreTab
          backups={backups}
          loadingBackups={loadingBackups}
          restoring={restoring}
          backupNote={backupNote}
          setBackupNote={setBackupNote}
          backupAlert={backupAlert}
          exportDataToExcel={exportDataToExcel}
          handleDownloadBackupJson={handleDownloadBackupJson}
          handleDownloadBackupExcel={handleDownloadBackupExcel}
          handleTakeManualBackup={handleTakeManualBackup}
          handleUploadBackupJson={handleUploadBackupJson}
          loadBackups={loadBackups}
          setRestoreModalBackup={setRestoreModalBackup}
          formatTrtDateTime={formatTrtDateTime}
        />
      )}

      {/* GERİ YÜKLEME (RESTORE) ONAY MODALI */}
      <RestoreConfirmModal
        restoreModalBackup={restoreModalBackup}
        setRestoreModalBackup={setRestoreModalBackup}
        restoring={restoring}
        handleConfirmRestore={handleConfirmRestore}
      />

      {/* MODAL 1: RESMİ TAHSİLAT MAKBUZU */}
      {receiptPayment && (() => {
        const s = allStudents.find(
          (st) =>
            (receiptPayment.student_id && String(st.id) === String(receiptPayment.student_id)) ||
            (receiptPayment.student_name && st.student_name && trIncludes(st.student_name, receiptPayment.student_name))
        );
        const stName = s?.student_name || receiptPayment.student_name || "";
        const rawParent = s?.parent_name || receiptPayment.parent_name || "";
        const cleanParent =
          rawParent && rawParent.trim().toLocaleUpperCase("tr-TR") !== stName.trim().toLocaleUpperCase("tr-TR")
            ? rawParent
            : "";
        return (
          <ReceiptModal
            payment={{
              ...receiptPayment,
              payment_date: receiptPayment.payment_date || receiptPayment.date || new Date().toISOString().slice(0, 10)
            }}
            student={{
              ...(s || {}),
              student_name: stName,
              parent_name: cleanParent,
              parent_phone: s?.parent_phone || s?.student_phone || receiptPayment.parent_phone || ""
            }}
            onClose={() => setReceiptPayment(null)}
          />
        );
      })()}

      {/* MODAL 2: RESMİ MATBU SENET (BONO) */}
      {promissoryStudent && (
        <PromissoryNoteModal
          student={promissoryStudent}
          onClose={() => setPromissoryStudent(null)}
        />
      )}

      {/* MODAL 3: HIZLI TAHSİLAT AL & MAKBUZ KES */}
      <QuickPaymentModal
        showQuickPaymentModal={showQuickPaymentModal}
        setShowQuickPaymentModal={setShowQuickPaymentModal}
        handleSavePayment={handleSavePayment}
        payStudentId={payStudentId}
        setPayStudentId={setPayStudentId}
        students={students}
        getEffectiveTotalFee={getEffectiveTotalFee}
        fmtMoney={fmtMoney}
        payAmount={payAmount}
        setPayAmount={setPayAmount}
        payMethod={payMethod}
        setPayMethod={setPayMethod}
        payBank={payBank}
        setPayBank={setPayBank}
        payNote={payNote}
        setPayNote={setPayNote}
        payLoading={payLoading}
      />

      {/* MODAL 4: GİDER EKLE (GAP-3: Hesap Seçimi ile Kasa Düşüşü) */}
      <ExpenseModal
        showExpenseModal={showExpenseModal}
        savingExpense={savingExpense}
        setShowExpenseModal={setShowExpenseModal}
        handleSaveExpense={handleSaveExpense}
        expTitle={expTitle}
        setExpTitle={setExpTitle}
        expAmount={expAmount}
        setExpAmount={setExpAmount}
        expAccount={expAccount}
        setExpAccount={setExpAccount}
        expCategory={expCategory}
        setExpCategory={setExpCategory}
        expDate={expDate}
        setExpDate={setExpDate}
      />

    </div>
  );
}
